<template>
  <div class="dropdown-table">
    <div v-if="label" class="subtitle-2 button-group-label">
      {{ label }}
      <tooltip v-if="!hideTooltip" :text="tooltip" />
      <helpIcon v-if="helpContent" :content="helpContent" />
    </div>
    <div class="dropdown-wrapper">
      <dropdown
        v-model="dropdownValue"
        :disabled="disabled"
        :items="dropdownItems"
        :prepend-inner-icon="icon"
        :warning="warning"
        :width="width"
      />
      <qvoButton
        :disabled="disabled || !dropdownValue"
        class="add-item-button"
        outlined
        leftIcon="mdi-plus-circle-outline"
        @click="addItem"
      >
        Add
      </qvoButton>
    </div>
    <dataTable
      :headers="tableHeaders"
      :items="tableItems"
      item-key="FieldId"
      no-data-text="No items added yet"
    >
      <template v-slot:item.type="{ item }">
        <div class="type">
          {{ item.Label }}
          <tooltip v-if="item.Tooltip" :text="item.Tooltip" />
          <helpIcon v-if="item.HelpContent" :content="item.HelpContent" />
        </div>
      </template>
      <template v-slot:item.rule="{ item }">
        <ruleConfigs
          :category="category"
          :id="ruleId"
          :item="item"
          :parentField="id"
          @setConfigValue="(value) => setRuleConfigValue({ item, value })"
        />
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn icon @click="confirmRemove(item)">
          <v-icon>mdi-delete-outline</v-icon>
        </v-btn>
      </template>
    </dataTable>
    <confirmationModal
      :value="itemToRemove.confirmationOpen"
      title="Delete"
      @closeModal="closeRemoveItemConfirmationModal"
    >
      <p class="subtitle-1">
        Are you sure you want to delete {{ itemToRemove.data.Label }}? This will
        be permanent.
      </p>
      <template #buttons>
        <qvoButton text @click="closeRemoveItemConfirmationModal"
          >Cancel</qvoButton
        >
        <qvoButton warning @click="removeItem">Delete</qvoButton>
      </template>
    </confirmationModal>
  </div>
</template>
  
  <script>
/**
 * @fileoverview Config input field for dropdown tables
 *
 * @author Cameron Bulock <cameron.bulock@quavo.com>
 */

import dataTable from "@/qux-common/qux22/components/organisms/data-table";
import dropdown from "@/qux-common/qux22/components/molecules/dropdown";
import helpIcon from "@/components/help-icon";
import qvoButton from "@/qux-common/qux22/components/molecules/button";
import confirmationModal from "@/components/confirmation-modal";
import tooltip from "@/components/tooltip";
import ruleConfigs from "./rule-configs";

export default {
  name: "DropdownTableField",
  components: {
    confirmationModal,
    dataTable,
    dropdown,
    helpIcon,
    tooltip,
    qvoButton,
    ruleConfigs,
  },
  props: {
    category: {
      type: String,
    },
    disabled: {
      type: Boolean,
    },
    helpContent: {
      type: String,
    },
    hideLabel: {
      type: Boolean,
    },
    hideTooltip: {
      type: Boolean,
    },
    icon: {
      type: String,
    },
    id: {
      type: String,
    },
    label: {
      type: String,
    },
    options: {
      type: Array,
    },
    parentField: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    ruleId: {
      type: String,
    },
    rules: {
      type: Array,
    },
    tooltip: {
      type: String,
    },
    value: {
      type: Object,
    },
    warning: {
      type: String,
    },
    width: {
      type: String,
    },
  },
  data() {
    return {
      tableHeaders: [
        { text: "Type", value: "type" },
        { text: "Rule", value: "rule" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      dropdownValue: null,
      itemToRemove: {
        data: {},
        confirmationOpen: false,
      },
    };
  },
  methods: {
    addItem() {
      const values = this.values;
      values[this.dropdownValue] = Object.create({});
      values[this.dropdownValue].IsEnabled = true;
      this.setConfigValue(values);
      this.dropdownValue = null;
    },
    closeRemoveItemConfirmationModal() {
      this.itemToRemove.confirmationOpen = false;
      this.itemToRemove.data = {};
    },
    confirmRemove(item) {
      this.itemToRemove.data = item;
      this.itemToRemove.confirmationOpen = true;
    },
    removeItem() {
      const values = this.values;
      // remove all existing values from this object
      values[this.itemToRemove.data.FieldId] = Object.create({});
      values[this.itemToRemove.data.FieldId].IsEnabled = false;
      this.setConfigValue(values);
      this.closeRemoveItemConfirmationModal();
    },
    setConfigValue(value) {
      this.$emit("setConfigValue", value);
    },
    setRuleConfigValue({ item, value }) {
      const values = this.values;
      const currentFieldValue = values[item.FieldId];

      values[item.FieldId] = { ...currentFieldValue, ...value };

      this.setConfigValue(values);
    },
  },
  computed: {
    dropdownItems() {
      const values = this.values;
      const items = this.options.map((option) => {
        // don't display values that are visible in the table in the dropdown
        if (
          option.FieldId &&
          (!values[option.FieldId] ||
            !values[option.FieldId].IsEnabled ||
            !values[option.FieldId].IsEnabled ||
            values[option.FieldId].IsEnabled === "false")
        ) {
          return {
            text: option.Label,
            value: option.FieldId,
          };
        }
      });
      return items;
    },
    tableItems() {
      const visibleItems = Object.entries(this.values)
        .filter(
          ([, value]) =>
            value.IsEnabled &&
            (value.IsEnabled === true || value.IsEnabled === "true")
        )
        .map(([key]) => this.options.find((option) => option.FieldId === key));
      return visibleItems;
    },
    values() {
      if (this.value && Object.keys(this.value).length) return this.value;

      const values = this.options.map((option) => ({
        [option.FieldId]: { IsEnabled: false },
      }));
      return Object.assign({}, ...values);
    },
  },
};
</script>
  
<style lang="scss" scoped>
.dropdown-table {
  display: flex;
  flex-direction: column;
  gap: $qvo-spacing-24;
}
.dropdown-wrapper {
  display: flex;
  align-items: center;
  gap: $qvo-spacing-24;
}

.v-data-table {
  &::v-deep {
    td {
      vertical-align: baseline;
    }
  }
}

.type {
  display: flex;
  align-items: center;
  gap: $qvo-spacing-8;
}
</style>